import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'sonner';
import { Separator } from "@/components/ui/separator";
import { Card, CardContent } from "@/components/ui/card";
import { BlogPost } from '@/types/blog';
import { TagManager } from '@/components/blog/TagManager';
import { WysiwygEditor } from '@/components/admin/WysiwygEditor';
import { supabase } from '@/integrations/supabase/client';

// Import our components
import { BlogEditorHeader } from './blog/BlogEditorHeader';
import { BlogMetaFields } from './blog/BlogMetaFields';
import { BlogImageFields } from './blog/BlogImageFields';
import { BlogPublishSettings } from './blog/BlogPublishSettings';
import { BlogPreview } from './blog/BlogPreview';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Eye, Edit } from 'lucide-react';
import { AuthorSelector } from './blog/AuthorSelector';

interface ImageAttribution {
  photographer?: string;
  photographerUrl?: string;
}

const BlogEditor = ({ initialPost }: { initialPost: BlogPost | null }) => {
  // State
  const [title, setTitle] = useState<string>(initialPost?.title || '');
  const [slug, setSlug] = useState<string>(initialPost?.slug || '');
  const [excerpt, setExcerpt] = useState<string>(initialPost?.excerpt || '');
  const [content, setContent] = useState<string>(initialPost?.content || '');
  const [coverImage, setCoverImage] = useState<string>(initialPost?.coverImage || '');
  const [publishDate, setPublishDate] = useState<string>(initialPost?.date || new Date().toLocaleDateString('en-CA'));
  const [isPublished, setIsPublished] = useState<boolean>(initialPost?.isPublished || false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageAttribution, setImageAttribution] = useState<ImageAttribution>({
    photographer: initialPost?.imageAttribution?.photographer || '',
    photographerUrl: initialPost?.imageAttribution?.photographerUrl || ''
  });
  const [tags, setTags] = useState<string[]>(initialPost?.tags || []);
  const [activeTab, setActiveTab] = useState<string>("edit");
  const [authorId, setAuthorId] = useState<string>(initialPost?.author_id || '');
  const [authorName, setAuthorName] = useState<string>(initialPost?.author || 'edgefi team');
  const [authorAvatar, setAuthorAvatar] = useState<string>('');
  const [authorBio, setAuthorBio] = useState<string>('');
  const [authorLinkedIn, setAuthorLinkedIn] = useState<string>('');

  const navigate = useNavigate();
  const isNewPost = !initialPost?.id;

  // Sync with initialPost changes
  useEffect(() => {
    if (initialPost) {
      setTitle(initialPost.title);
      setSlug(initialPost.slug);
      setExcerpt(initialPost.excerpt);
      setContent(initialPost.content);
      setCoverImage(initialPost.coverImage || '');
      setIsPublished(initialPost.isPublished || false);
      setPublishDate(initialPost.date || new Date().toLocaleDateString('en-CA'));
      setTags(initialPost.tags || []);
      setAuthorName(initialPost.author || 'edgefi team');
      setAuthorId(initialPost.author_id || '');
      setImageAttribution({
        photographer: initialPost.imageAttribution?.photographer || '',
        photographerUrl: initialPost.imageAttribution?.photographerUrl || ''
      });
    }
  }, [initialPost]);

  // Handle author change
  const handleAuthorChange = (
    newAuthorId: string, 
    newAuthorName: string, 
    newAuthorAvatar?: string,
    newAuthorBio?: string,
    newAuthorLinkedIn?: string
  ) => {
    setAuthorId(newAuthorId);
    setAuthorName(newAuthorName);
    setAuthorAvatar(newAuthorAvatar || '');
    setAuthorBio(newAuthorBio || '');
    setAuthorLinkedIn(newAuthorLinkedIn || '');
    setIsDirty(true);
  };

  // Event handlers
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
    setIsDirty(true);
  };

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSlug(e.target.value);
    setIsDirty(true);
  };

  const handleExcerptChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setExcerpt(e.target.value);
    setIsDirty(true);
  };

  const handleContentChange = (newContent: string) => {
    setContent(newContent);
    setIsDirty(true);
  };

  const handleCoverImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setCoverImage(URL.createObjectURL(file));
      setIsDirty(true);
    }
  };

  const handlePublishDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPublishDate(e.target.value);
    setIsDirty(true);
  };

  const handleIsPublishedChange = (checked: boolean) => {
    setIsPublished(checked);
    setIsDirty(true);
  };

  const handleImageAttributionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setImageAttribution(prev => ({ ...prev, [name]: value }));
    setIsDirty(true);
  };

  const handleTagsChange = (updatedTags: string[]) => {
    setTags(updatedTags);
    setIsDirty(true);
  };

  const handleTagsUpdate = async (postId: number, newTags: string[]): Promise<boolean> => {
    try {
      console.log("Updating tags for post", postId, "with tags:", newTags);
      
      const { data: existingTags, error: tagsError } = await supabase
        .from('blog_tags')
        .select('id, name');
        
      if (tagsError) throw tagsError;
      
      const tagMap = new Map();
      existingTags?.forEach(tag => tagMap.set(tag.name.toLowerCase(), tag.id));
      
      const tagsToCreate = newTags.filter(tag => !tagMap.has(tag.toLowerCase()));
      
      for (const tagName of tagsToCreate) {
        const { data: newTag, error } = await supabase
          .from('blog_tags')
          .insert({ name: tagName })
          .select('id, name')
          .single();
          
        if (error) throw error;
        if (newTag) tagMap.set(newTag.name.toLowerCase(), newTag.id);
      }
      
      const tagIdsForPost = newTags.map(tag => {
        const tagId = tagMap.get(tag.toLowerCase());
        if (!tagId) {
          console.error(`Could not find ID for tag: ${tag}`);
          return null;
        }
        return tagId;
      }).filter(Boolean);
      
      const { error: removeError } = await supabase
        .from('blog_post_tags')
        .delete()
        .eq('post_id', postId);
      
      if (removeError) throw removeError;
      
      const tagAssociations = tagIdsForPost.map(tagId => ({
        post_id: postId,
        tag_id: tagId
      }));
      
      if (tagAssociations.length > 0) {
        const { error: insertError } = await supabase
          .from('blog_post_tags')
          .insert(tagAssociations);
              
        if (insertError) throw insertError;
      }
      
      console.log("Tags updated successfully");
      return true;
    } catch (error) {
      console.error("Error updating tags:", error);
      toast.error("Failed to update tags for the post.");
      return false;
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);

      if (!title || !slug || !excerpt || !content) {
        toast.error("Please fill in all required fields.");
        return;
      }

      const imageName = imageFile ? `${uuidv4()}-${imageFile.name}` : null;
      let publicUrl: string | null = coverImage;

      if (imageFile) {
        const { data, error } = await supabase
          .storage
          .from('blog-images')
          .upload(imageName!, imageFile, {
            cacheControl: '3600',
            upsert: false
          });

        if (error) {
          console.error("Error uploading image:", error);
          toast.error("Failed to upload image. Please try again.");
          return;
        }

        publicUrl = `https://hzrsmwtpfpkidrdqnvbx.supabase.co/storage/v1/object/public/blog-images/${imageName}`;
      }

      const postData = {
        title: title,
        slug: slug,
        post_summary: excerpt,
        content: content,
        featured_image: publicUrl,
        publish_date: publishDate,
        is_published: isPublished,
        author: authorName,
        author_id: authorId || null
      };

      let savedPost;
      if (isNewPost) {
        const { data: newPost, error } = await supabase
          .from('blog_posts')
          .insert([postData])
          .select()
          .single();

        if (error) {
          console.error("Error creating blog post:", error);
          toast.error("Failed to create blog post. Please try again.");
          return;
        }
        
        savedPost = newPost;
        
        // Ensure postId is a number before passing to handleTagsUpdate
        const postId = typeof newPost.id === 'string' ? parseInt(newPost.id, 10) : newPost.id;
        
        if (Number.isNaN(postId)) {
          console.error("Invalid post ID:", newPost.id);
          toast.error("Error processing post ID. Tags could not be updated.");
        } else {
          const tagsUpdated = await handleTagsUpdate(postId, tags);
          if (!tagsUpdated) {
            toast.warning("Post was created but there was an issue with tags");
          }
        }
      } else {
        // Fix the remaining error - convert initialPost.id to number before using it
        const updatePostId = typeof initialPost!.id === 'string' ? parseInt(initialPost!.id, 10) : initialPost!.id;
        
        if (Number.isNaN(updatePostId)) {
          console.error("Invalid post ID for update:", initialPost!.id);
          toast.error("Error processing post ID. Post could not be updated.");
          setIsSaving(false);
          return;
        }
        
        const { data: updatedPost, error: updateError } = await supabase
          .from('blog_posts')
          .update({
            title: title,
            slug: slug,
            post_summary: excerpt,
            content: content,
            featured_image: publicUrl,
            publish_date: publishDate,
            is_published: isPublished,
            author: authorName,
            author_id: authorId || null
          })
          .eq('id', updatePostId)
          .select()
          .single();
          
        if (updateError) {
          throw updateError;
        }
        
        savedPost = updatedPost;
        
        // This line had the error - convert id to number properly
        const tagsUpdated = await handleTagsUpdate(updatePostId, tags);
        if (!tagsUpdated) {
          toast.warning("Post was updated but there was an issue with tags");
        }
      }

      if (savedPost) {
        if (imageAttribution.photographer || imageAttribution.photographerUrl) {
          if (!isNewPost) {
            // We need to ensure initialPost.id is handled as a number
            const deletePostId = typeof initialPost!.id === 'string' ? parseInt(initialPost!.id, 10) : initialPost!.id;
            
            const { error: deleteError } = await supabase
              .from('blog_image_attribution')
              .delete()
              .eq('post_id', deletePostId);

            if (deleteError) {
              console.error("Error deleting existing image attribution:", deleteError);
              toast.error("Failed to update image attribution. Please try again.");
              return;
            }
          }

          // Fix: Ensure post ID is a number for image attribution
          const postIdForAttribution = typeof savedPost.id === 'string' ? parseInt(savedPost.id, 10) : savedPost.id;
          
          if (!Number.isNaN(postIdForAttribution)) {
            const { error: insertError } = await supabase
              .from('blog_image_attribution')
              .insert([{
                post_id: postIdForAttribution,
                photographer: imageAttribution.photographer,
                photographer_url: imageAttribution.photographerUrl
              }]);

            if (insertError) {
              console.error("Error saving image attribution:", insertError);
              toast.error("Failed to save image attribution. Please try again.");
              return;
            }
          } else {
            console.error("Invalid post ID for attribution:", savedPost.id);
            toast.warning("Post was saved but image attribution could not be updated");
          }
        }

        toast.success(`Blog post ${isNewPost ? 'created' : 'updated'} successfully!`);
        navigate('/admin/blog');
      }
    } catch (error) {
      console.error("Error saving blog post:", error);
      toast.error("Failed to save blog post. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      <BlogEditorHeader 
        isSaving={isSaving} 
        isDirty={isDirty} 
        isNewPost={isNewPost}
        onSave={handleSave}
      />
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-2 w-52 mb-6">
          <TabsTrigger value="edit" className="flex items-center gap-2">
            <Edit size={16} />
            <span>Edit</span>
          </TabsTrigger>
          <TabsTrigger value="preview" className="flex items-center gap-2">
            <Eye size={16} />
            <span>Preview</span>
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="edit" className="space-y-0">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              {/* Main content section */}
              <Card>
                <CardContent className="p-6 space-y-6">
                  <BlogMetaFields
                    title={title}
                    slug={slug}
                    excerpt={excerpt}
                    onTitleChange={handleTitleChange}
                    onSlugChange={handleSlugChange}
                    onExcerptChange={handleExcerptChange}
                    disabled={isSaving}
                  />
                  
                  <Separator className="my-6" />
                  
                  <div>
                    <label className="block text-base font-medium mb-2">Content</label>
                    <WysiwygEditor
                      value={content}
                      onChange={handleContentChange}
                      placeholder="Write your blog post content here..."
                    />
                  </div>
                </CardContent>
              </Card>
              
              <BlogImageFields
                coverImage={coverImage}
                imageAttribution={imageAttribution}
                onCoverImageChange={handleCoverImageChange}
                onImageAttributionChange={handleImageAttributionChange}
                disabled={isSaving}
              />
            </div>
            
            <div className="space-y-6">
              {/* Sidebar components */}
              <BlogPublishSettings
                publishDate={publishDate}
                isPublished={isPublished}
                onPublishDateChange={handlePublishDateChange}
                onIsPublishedChange={handleIsPublishedChange}
                disabled={isSaving}
              />
              
              <AuthorSelector
                currentAuthorId={authorId}
                currentAuthorName={authorName}
                onAuthorChange={handleAuthorChange}
                disabled={isSaving}
              />
              
              <Card>
                <CardContent className="p-6 space-y-4">
                  <label className="block font-medium">Tags</label>
                  <TagManager 
                    initialTags={tags} 
                    onTagsChange={handleTagsChange}
                    disabled={isSaving} 
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Tags help users find related content
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="preview">
          <div className="max-w-4xl mx-auto">
            <BlogPreview
              title={title}
              content={content}
              coverImage={coverImage}
              date={publishDate}
              author={authorName}
              authorAvatar={authorAvatar}
              authorBio={authorBio}
              authorLinkedIn={authorLinkedIn}
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BlogEditor;
